"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { readCookieConsent } from "@/components/analytics/browser-attribution";
import { COOKIE_CONSENT_NAME } from "@/lib/cookie-consent";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";

export const MICROSOFT_CLARITY_PROJECT_ID = "wsz57cz325";

const LIVE_CLARITY_HOSTS = new Set([
  "thesitebook.co.uk",
  "www.thesitebook.co.uk",
  "do.thesitebook.co.uk",
]);

declare global {
  interface Window {
    clarity?: ClarityFunction;
  }
}

type ClarityFunction = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

function hostnameFromBaseUrl(baseUrl: string | undefined): string | null {
  if (!baseUrl) return null;

  try {
    const url = baseUrl.includes("://") ? baseUrl : `https://${baseUrl}`;
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function normalizedHostname(hostname: string | undefined): string | null {
  return hostname ? hostname.toLowerCase() : null;
}

function isLiveClarityHost(hostname: string | null): boolean {
  return hostname !== null && LIVE_CLARITY_HOSTS.has(hostname);
}

export function shouldLoadMicrosoftClarity(
  baseUrl = process.env.NEXT_PUBLIC_APP_BASE_URL,
  browserHostname?: string,
): boolean {
  return (
    isLiveClarityHost(hostnameFromBaseUrl(baseUrl)) ||
    isLiveClarityHost(normalizedHostname(browserHostname))
  );
}

function getServerClarityEnabled() {
  return shouldLoadMicrosoftClarity();
}

function getClientClarityEnabled() {
  if (typeof window === "undefined") return getServerClarityEnabled();
  return shouldLoadMicrosoftClarity(
    process.env.NEXT_PUBLIC_APP_BASE_URL,
    window.location.hostname,
  );
}

// useSyncExternalStore subscribe for both env (one-shot, no real updates) and
// consent (real updates via cookie_consent_change + storage events). Server
// snapshot always returns `null` for consent so the first render matches what
// the server painted (which doesn't have access to the cookie); the client
// snapshot then flips it once useSyncExternalStore re-renders post-hydration.
function subscribeNoop() {
  return () => {};
}

function subscribeToConsent(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === COOKIE_CONSENT_NAME) cb();
  };
  window.addEventListener("cookie_consent_change", cb);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("cookie_consent_change", cb);
    window.removeEventListener("storage", onStorage);
  };
}

function getServerConsentSnapshot(): "accepted" | "declined" | null {
  return null;
}

function getClientConsentSnapshot(): "accepted" | "declined" | null {
  return readCookieConsent();
}

export function MicrosoftClarity() {
  const envEnabled = useSyncExternalStore(
    subscribeNoop,
    getClientClarityEnabled,
    getServerClarityEnabled,
  );
  // Never load Clarity on bearer-token paths (or /login|/signup carrying a
  // token in ?redirect_url) — it session-records the full URL, token included.
  // See lib/analytics/suppressed-paths.ts.
  const pathname = usePathname();
  const suppressed = useAnalyticsSuppressed(pathname);
  const enabled = envEnabled && !suppressed;

  const consent = useSyncExternalStore(
    subscribeToConsent,
    getClientConsentSnapshot,
    getServerConsentSnapshot,
  );

  useEffect(() => {
    if (!enabled) {
      // Suppressed route (or Clarity disabled). A consented visitor may have
      // loaded Clarity on a prior page; a client-side navigation INTO a token
      // route must actively STOP the recorder — denied consent alone still
      // permits Clarity's limited/no-consent page tracking of the token-bearing
      // URL. (Audit P1, symmetric to the GA ga-disable kill switch.)
      window.clarity?.("stop");
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
      return;
    }

    if (consent !== "accepted") {
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
      return;
    }

    let active = true;
    let script = document.getElementById(
      "microsoft-clarity-lib",
    ) as HTMLScriptElement | null;

    if (!window.clarity) {
      const clarity = ((...args: unknown[]) => {
        (clarity.q ??= []).push(args);
      }) as ClarityFunction;
      window.clarity = clarity;
    }

    function applyCurrentConsent(): boolean {
      const accepted = active && readCookieConsent() === "accepted";
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: accepted ? "granted" : "denied",
      });
      return accepted;
    }

    function onLoad() {
      if (script) script.dataset.loaded = "true";
      applyCurrentConsent();
    }

    // Start every pending library in denied mode. A detached script can still
    // finish executing after removal, so its real queue must contain no stale
    // grant that could be drained before our load handler runs.
    window.clarity("consentv2", {
      ad_Storage: "denied",
      analytics_Storage: "denied",
    });

    // PECR/ICO: do not even fetch the Clarity library unless the current,
    // fail-closed cookie read still says accepted. Grant only from onLoad,
    // after the delayed library has executed and we have re-read the cookie.
    if (readCookieConsent() !== "accepted") return;

    if (script?.dataset.loaded === "true") {
      // Resume in case a prior suppressed-route visit stopped the recorder.
      window.clarity?.("start");
      applyCurrentConsent();
    } else {
      if (!script) {
        script = document.createElement("script");
        script.id = "microsoft-clarity-lib";
        script.async = true;
        script.src = `https://www.clarity.ms/tag/${MICROSOFT_CLARITY_PROJECT_ID}`;
        script.addEventListener("load", onLoad, { once: true });
        document.head.appendChild(script);
      } else {
        script.addEventListener("load", onLoad, { once: true });
      }
    }

    return () => {
      active = false;
      script?.removeEventListener("load", onLoad);
      if (script?.dataset.loaded !== "true") script?.remove();
      window.clarity?.("consentv2", {
        ad_Storage: "denied",
        analytics_Storage: "denied",
      });
    };
  }, [consent, enabled]);

  return null;
}
